import { AutomaticStartBadge } from '@/components/projects/chains/runs/AutomaticStartBadge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useChainRunsForWorkItem } from '@/hooks'
import { formatDateTime } from '@/lib/timeFormatters'
import { Loader2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { WorkflowStatusBadge } from './shared'

interface WorkItemChainRunsProps {
  projectId: string
  workItemId: string
}

export function WorkItemChainRuns({ projectId, workItemId }: WorkItemChainRunsProps) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useChainRunsForWorkItem(projectId, workItemId)
  const chainRuns = data?.items ?? []

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" data-testid="work-item-chain-runs-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading chain runs…
      </div>
    )
  }

  if (isError) {
    return (
      <p role="alert" className="text-sm text-danger-foreground" data-testid="work-item-chain-runs-error">
        Chain runs for this work item could not be loaded.
      </p>
    )
  }

  if (chainRuns.length === 0) return null

  return (
    <section className="space-y-3" data-testid="work-item-chain-runs">
      <h3 className="text-lg font-medium text-card-foreground">Chain runs</h3>
      <div className="rounded-md border">
        <Table aria-label="Chain runs for this work item">
          <TableHeader>
            <TableRow>
              <TableHead>Chain</TableHead>
              <TableHead className="w-[150px]">Status</TableHead>
              <TableHead className="w-[90px]">Links</TableHead>
              <TableHead className="w-[190px]">Started</TableHead>
              <TableHead className="w-[220px]">User</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {chainRuns.map(run => {
              const runPath = `/projects/${projectId}/workflows/runs/chains/${run.id}`
              return (
                <TableRow
                  key={run.id}
                  data-testid="work-item-chain-run-row"
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(runPath)}
                >
                  <TableCell>
                    <Link to={runPath} className="font-medium hover:underline" onClick={event => event.stopPropagation()}>
                      {run.chainDisplayName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <WorkflowStatusBadge status={run.status} />
                  </TableCell>
                  <TableCell className="text-sm">
                    {run.completedLinks}/{run.totalLinks}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(run.startedAt)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 text-sm">{run.triggeredBy}</span>
                      <AutomaticStartBadge triggerSource={run.triggerSource} />
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}
